/**
 * Defines some functions that perform geometric computations
 *
 * @module MW/layout/Geometry
 * @copyright Copyright 2014-2015 The MathWorks, Inc.
 */
define(["dojo/dom-geometry",
        "dojo/dom-style"],
    function (domGeometry, domStyle) {
    return {

        /**
         * Copies the values defining one box to another
         *
         * @param originalBox {{l: number, t: number, w: number, h: number}} - the source box
         * @param duplicateBox {{l: number, t: number, w: number, h: number}} - the destination box
         * @static
         */
        copyBox: function (originalBox, duplicateBox) {
            duplicateBox.l = originalBox.l;
            duplicateBox.t = originalBox.t;
            duplicateBox.w = originalBox.w;
            duplicateBox.h = originalBox.h;
        },

        /**
         * @param box {{l: number, t: number, w: number, h: number}} - box coordinates and dimensions
         * @param x {number} - x coordinate
         * @param y {number} - y coordinate
         * @returns {boolean} true if the point is within the box
         * @static
         */
        boxContains: function (box, x, y) {
            return x >= box.l && x <= (box.l + box.w) && y >= box.t && y <= (box.t + box.h);
        },

        /**
         *  Returns the clipping rectangle that must be applied to the given box to constrain it to the specified bounds.
         *
         * @param box {Object} - the box to be clipped
         * @param bounds {Object} - the bound to clip it to
         * @returns {{l: number, t: number, w: number, h: number}}
         * @static
         */
        computeClip: function (box, bounds) {
            var boxRight = box.l + box.w;
            var boundsRight = bounds.l + bounds.w;
            var boxBottom = box.t + box.h;
            var boundsBottom = bounds.t + bounds.h;
            var clip = {l: 0, t: 0, w: box.w, h: box.h};
            if (box.l < bounds.l) {
                clip.l = bounds.l - box.l;
                clip.w -= clip.l;
            }
            if (box.t < bounds.t) {
                clip.t = bounds.t - box.t;
                clip.h -= clip.t;
            }
            if (boxRight > boundsRight) {
                clip.w -= boxRight - boundsRight;
            }
            if (boxBottom > boundsBottom) {
                clip.h -= boxBottom - boundsBottom;
            }
            return clip;
        },

        /**
         * Computes the intersection of two boxes.  If either or both of the returned w and h properties
         * are negative then then intersection is empty (see the boxesOverlap method).
         *
         * @param box1 {{l: number, t: number, w: number, h: number}}
         * @param box2 {{l: number, t: number, w: number, h: number}}
         * @returns {{l: number, t: number, w: number, h: number}}
         * @static
         */
        intersectionBox: function (box1, box2) {
            var l = Math.max(box1.l, box2.l);
            var t = Math.max(box1.t, box2.t);
            var r = Math.min(box1.l + box1.w, box2.l + box2.w);
            var b = Math.min(box1.t + box1.h, box2.t + box2.h);
            return {l: l, t: t, w: r - l, h: b - t};
        },

        /**
         * Computes the union of two boxes, i.e. the smallest box that contains both of the given boxes.
         *
         * @param box1 {{l: number, t: number, w: number, h: number}}
         * @param box2 {{l: number, t: number, w: number, h: number}}
         * @returns {{l: number, t: number, w: number, h: number}}
         * @static
         */
        unionBox: function (box1, box2) {
            var l = Math.min(box1.l, box2.l);
            var t = Math.min(box1.t, box2.t);
            var r = Math.max(box1.l + box1.w, box2.l + box2.w);
            var b = Math.max(box1.t + box1.h, box2.t + box2.h);
            return {l: l, t: t, w: r - l, h: b - t};
        },

        constrainBox: function (boxToConstrain, boundingBox) {
            if (boxToConstrain.l < boundingBox.l) {
                boxToConstrain.l = boundingBox.l;
            }
            if (boxToConstrain.t < boundingBox.t) {
                boxToConstrain.t = boundingBox.t;
            }
            if (boxToConstrain.l + boxToConstrain.w > boundingBox.l + boundingBox.w) {
                boxToConstrain.l = boundingBox.l + boundingBox.w - boxToConstrain.w;
            }
            if (boxToConstrain.t + boxToConstrain.h > boundingBox.t + boundingBox.h) {
                boxToConstrain.t = boundingBox.t + boundingBox.h - boxToConstrain.h;
            }
        },

        /**
         * Determines if two boxes overlap each other.
         *
         * @param box1 {{l: number, t: number, w: number, h: number}}
         * @param box2 {{l: number, t: number, w: number, h: number}}
         * @returns {boolean} true if the boxes overlap
         * @static
         */
        boxesOverlap: function (box1, box2) {
            var overlapBox = this.intersectionBox(box1, box2);
            return overlapBox.w > 0 && overlapBox.h > 0;
        },

        areBoxesEqual: function (box1, box2) {
            return box1.l === box2.l &&
                   box1.t === box2.t &&
                   box1.w === box2.w &&
                   box1.h === box2.h;
        },

        /**
         * Determine if a point is either inside or within specified x and y distances of a given box
         *
         * @param box {{l: number, t: number, w: number, h: number}}
         * @param x {number}
         * @param y {number}
         * @param xThreshold {number}
         * @param yThreshold {number}
         * @returns {boolean} true if the point is near the box
         * @static
         */
        isNearBox: function (box, x, y, xThreshold, yThreshold) {
            yThreshold = yThreshold || xThreshold;
            return x >= (box.l - xThreshold) &&
                   x <= (box.l + box.w + xThreshold) &&
                   y >= (box.t - yThreshold) &&
                   y <= (box.t + box.h + yThreshold);
        },

        /**
         * Determines the box edge to which a given point is closest.  If the point is not within the specified
         * margin of any edge then "center" is returned.
         * @param box {{l: number, t: number, w: number, h: number}}
         * @param x {number}
         * @param y {number}
         * @param margin {number}
         * @returns {string} - the closest edge ("left", "right", "top", "bottom", "center")
         * @static
         */
        getNearestBorder: function (box, x, y, margin) {
            var xMargin = margin * box.w;
            var yMargin = margin * box.h;
            var hx, wy, aboveDiagonal1, aboveDiagonal2;
            x -= box.l;
            y -= box.t;
            if (x > xMargin && x < (box.w - xMargin) && y > yMargin && y < (box.h - yMargin)) {
                return "center";
            }
            hx = box.h * x;
            wy = box.w * y;
            aboveDiagonal1 = hx > wy;
            aboveDiagonal2 = hx < (box.w * box.h - wy);
            return aboveDiagonal1 ? (aboveDiagonal2 ? "top" : "right") : (aboveDiagonal2 ? "left" : "bottom");
        },

        /**
         * Determines the opposite of a given edge
         *
         * @param region {string} "top", "bottom", "left" or "right"
         * @returns {string} the opposing region
         * @static
         */
        oppositeRegion: function (region) {
            switch (region) {
                case "top":
                    return "bottom";
                case "bottom":
                    return "top";
                case "left":
                    return "right";
                case "right":
                    return "left";
            }
            return "center";
        },

        measure: function (childNode, parentNode) {
            var addedToParent;
            if (!childNode.parentNode) {
                domStyle.set(childNode, {visibility: "hidden", position: "absolute"});
                parentNode.appendChild(childNode);
                addedToParent = true;
            }
            var size = domGeometry.getMarginBox(childNode);
            if (addedToParent) {
                parentNode.removeChild(childNode);
                domStyle.set(childNode, {visibility: "visible"});
            }
            return size;
        }
    };
});